import React, { useCallback, useEffect, useState } from "react";
import { Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import {
  SectionCard,
  Divider,
  PrimaryButton,
  SecondaryButton
} from "@/components/ds";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import IntegrationActivityLog from "../IntegrationActivityLog";

// ── Types ────────────────────────────────────────────────────────────────────

interface VoiceConfig {
  siteId: number;
  providerSlug: string;
  hasWebhookSecret: boolean;
}

interface VoiceAgent {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneSettings?: {
    providerAgentId?: string;
    availableForCalls?: boolean;
  };
}

const EMPTY_CONFIG: VoiceConfig = {
  siteId: 0,
  providerSlug: "",
  hasWebhookSecret: false
};

const SETUP_STEPS = [
  "In Zoom App Marketplace, open your app → Event Subscriptions → Add New Event Subscription.",
  "Set the Webhook URL to the value shown below.",
  "Subscribe to events: phone.callee_ringing, phone.callee_answered, phone.callee_ended, phone.callee_missed, phone.callee_rejected.",
  "Copy the secret token and paste it into the Webhook Secret field below.",
  "In the Agent Mapping tab, enter each agent's Zoom User ID (Zoom Admin → User Management → Users → click user)."
];

// ── Overview tab ─────────────────────────────────────────────────────────────

function OverviewCard() {
  return (
    <>
      <SectionCard title="Zoom Phone Screen Pop" description="Customer context in Velaro while call audio and controls remain in Zoom Phone." className="mb-4">
        <div className="flex gap-2 mb-4">
          {["Caller matching", "NetSuite context", "Web and mobile"].map((b) => (
            <span key={b} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              {b}
            </span>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Connect your Zoom Phone system to get a full customer screen pop on every inbound call. When a call
          arrives, Velaro automatically searches your connected CRM, service, and e-commerce integrations and
          surfaces matching customer data in the agent sidebar — before they answer.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-700">
            <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1">Inbound call screen pop</div>
            <div className="text-xs text-zinc-500">
              When a Zoom Phone call arrives, Velaro searches every connected integration (NetSuite, HubSpot,
              Salesforce, ServiceTitan, and more) and shows the caller's profile in the agent sidebar.
            </div>
          </div>
          <div className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-700">
            <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1">NetSuite customer context</div>
            <div className="text-xs text-zinc-500">
              When NetSuite screen pop is configured, customer match and record details appear with the call.
            </div>
          </div>
          <div className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-700">
            <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1">Zoom owns the call</div>
            <div className="text-xs text-zinc-500">
              Agents answer, transfer, and manage call audio in Zoom Phone. Velaro never takes control of the call.
            </div>
          </div>
          <div className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-700">
            <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1">Webhook-based — no hardware</div>
            <div className="text-xs text-zinc-500">
              Uses Zoom's own Phone Webhooks API. Agents keep using their existing Zoom Phone app; nothing new
              to install.
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Setup process" description="Three steps to a working screen pop." className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { n: 1, t: "Subscribe to events in Zoom", d: "Add an Event Subscription in Zoom App Marketplace pointing at the Webhook URL on the Setup tab." },
            { n: 2, t: "Map each agent", d: "Enter every agent's Zoom User ID in the Agent Mapping tab so calls route to the right person." },
            { n: 3, t: "Open Velaro for context", d: "Keep handling the call in Zoom Phone while Velaro shows the matched customer context." }
          ].map((s) => (
            <div key={s.n} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-5 h-5 rounded-full bg-zinc-800 text-white text-[10px] font-bold flex items-center justify-center shrink-0 dark:bg-zinc-200 dark:text-zinc-900">
                  {s.n}
                </span>
                <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{s.t}</span>
              </div>
              <p className="text-xs text-zinc-500">{s.d}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function ZoomPhone() {
  const api = useApi();
  const toastAlert = useToastAlert();

  const [activeTab, setActiveTab] = useState<"overview" | "setup" | "agents" | "log">("overview");

  const [config, setConfig] = useState<VoiceConfig>(EMPTY_CONFIG);
  const [webhookSecret, setWebhookSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [savingAgentId, setSavingAgentId] = useState<number | null>(null);
  const [agentEdits, setAgentEdits] = useState<Record<number, string>>({});
  const [confirmProviderSwitch, setConfirmProviderSwitch] = useState(false);


  const isZoomActive = config.providerSlug === "zoom";

  // ── Load config (implicitly sets provider to "zoom" — this page IS the
  //    Zoom-specific config surface, unlike the generic multi-provider Voice
  //    admin panel this replaces the discoverable path to). ──────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("voice/admin/config");
      if (resp.ok) {
        const data = await resp.json();
        setConfig({
          siteId: data.siteId ?? 0,
          providerSlug: data.providerSlug ?? "",
          // A secret from another active provider must never be represented as a saved
          // Zoom secret. Switching is an explicit, destructive admin action below.
          hasWebhookSecret: data.providerSlug === "zoom" && !!(data.webhookSecret || data.hasWebhookSecret)
        });
      }
    } catch {
      // safe default
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    setAgentsError(null);
    try {
      const resp = await api.messaging.get("voice/admin/agents?providerSlug=zoom");
      if (resp.ok) {
        const list: VoiceAgent[] = Array.isArray(await resp.clone().json()) ? await resp.json() : [];
        setAgents(list);
        const edits: Record<number, string> = {};
        list.forEach((a) => {
          edits[a.id] = a.phoneSettings?.providerAgentId ?? "";
        });
        setAgentEdits(edits);
      } else if (resp.status === 403) {
        setAgentsError("Only site administrators can view or edit agent phone mapping.");
      } else {
        setAgentsError(`Failed to load agents (${resp.status}).`);
      }
    } catch (err) {
      setAgentsError(`Failed to load agents: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAgentsLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeTab === "agents") loadAgents();
  }, [activeTab, loadAgents]);


  async function readErrorMessage(resp: Response, fallback: string): Promise<string> {
    try {
      const body = await resp.clone().json();
      return typeof body?.error === "string" && body.error ? body.error : `${fallback} (${resp.status})`;
    } catch {
      return `${fallback} (${resp.status})`;
    }
  }

  async function handleSaveWebhook() {
    const replacingAnotherProvider = !!config.providerSlug && config.providerSlug !== "zoom";
    if (replacingAnotherProvider && !confirmProviderSwitch) {
      toastAlert.displayToast(Severity.Error, "Confirm replacement of the existing voice provider before saving Zoom Phone.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.messaging.post("voice/admin/config", {
        ...config,
        providerSlug: "zoom",
        allowProviderSwitch: replacingAnotherProvider,
        webhookSecret: webhookSecret || undefined
      });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Zoom Phone webhook settings saved.");
        setWebhookSecret("");
        load();
      } else {
        toastAlert.displayToast(Severity.Error, await readErrorMessage(resp, "Failed to save webhook settings."));
      }
    } catch (err) {
      toastAlert.displayToast(
        Severity.Error,
        `Failed to save webhook settings: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAgent(agentId: number) {
    setSavingAgentId(agentId);
    try {
      const providerAgentId = agentEdits[agentId] ?? "";
      const resp = await api.messaging.post(`voice/admin/agents/${agentId}/mapping?providerSlug=zoom`, { providerAgentId });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Agent mapping saved.");
      } else {
        toastAlert.displayToast(Severity.Error, await readErrorMessage(resp, "Failed to save agent mapping."));
      }
    } catch (err) {
      toastAlert.displayToast(
        Severity.Error,
        `Failed to save agent mapping: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setSavingAgentId(null);
    }
  }

  const messagingApiBase = ((import.meta.env.VITE_MESSAGING_API_URL as string) ?? "").replace(/\/$/, "");
  const webhookUrl = config.siteId ? `${messagingApiBase}/ZoomPhoneWebhook/${config.siteId}` : "";

  const tabsJSX = (
    <>
      <IntegrationTab active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>
        Overview
      </IntegrationTab>
      <IntegrationTab active={activeTab === "setup"} onClick={() => setActiveTab("setup")}>
        Setup
      </IntegrationTab>
      <IntegrationTab active={activeTab === "agents"} onClick={() => setActiveTab("agents")}>
        Agent Mapping
      </IntegrationTab>
      <IntegrationTab active={activeTab === "log"} onClick={() => setActiveTab("log")}>
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Zoom Phone" tabs={tabsJSX}>
      {activeTab === "overview" && <OverviewCard />}

      {activeTab === "setup" && (
        <>
          <SectionCard
            title="Connect Zoom Phone"
            description="Passive screen pop via Zoom's Phone Webhooks API."
            className="mb-4"
          >
            {loading ? (
              <div className="text-sm text-zinc-400 py-4">Loading…</div>
            ) : (
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-900 mb-1">Webhook URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={webhookUrl || "Save once to generate your webhook URL"}
                      className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-zinc-50 text-zinc-600 font-mono dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200"
                    />
                    {webhookUrl && (
                      <SecondaryButton
                        className="!text-xs !px-3 !py-2"
                        onClick={() => {
                          navigator.clipboard?.writeText(webhookUrl);
                          toastAlert.displayToast(Severity.Success, "Webhook URL copied.");
                        }}
                      >
                        Copy
                      </SecondaryButton>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="zoomWebhookSecret" className="block text-sm font-medium text-zinc-900 mb-1">
                    Webhook Secret
                  </label>
                  <input
                    id="zoomWebhookSecret"
                    type="password"
                    placeholder={
                      config.hasWebhookSecret
                        ? "••••••••••••• (saved — enter new value to replace)"
                        : "Paste the secret token from your Zoom Event Subscription"
                    }
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                  />
                  {config.hasWebhookSecret && (
                    <span className="text-xs text-green-600 font-medium mt-1 inline-block">✓ Saved</span>
                  )}
                </div>

                {config.providerSlug && !isZoomActive && (
                  <label className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                    <input
                      type="checkbox"
                      checked={confirmProviderSwitch}
                      onChange={(event) => setConfirmProviderSwitch(event.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      Replace the active <strong>{config.providerSlug}</strong> voice provider with Zoom Phone. Its saved voice credentials and webhook secret will be cleared.
                    </span>
                  </label>
                )}

                <div>
                  <PrimaryButton onClick={handleSaveWebhook} disabled={saving}>
                    {saving ? "Saving…" : "Save Webhook Settings"}
                  </PrimaryButton>
                </div>

                <Divider />

                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Setup steps</p>
                  <ol className="flex flex-col gap-2">
                    {SETUP_STEPS.map((step, i) => (
                      <li key={i} className="flex gap-2.5 text-xs text-zinc-600 dark:text-zinc-300">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="leading-relaxed pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                    <strong>Note:</strong> Zoom signs webhooks with x-zm-signature (HMAC-SHA256). The secret token
                    must match your Zoom app.
                  </p>
                </div>
              </div>
            )}
          </SectionCard>

        </>
      )}

      {activeTab === "agents" && (
        <SectionCard title="Agent Phone Mapping" description="Enter each agent's Zoom User ID so Velaro can match inbound calls to the right agent.">
          {agentsLoading ? (
            <div className="text-sm text-zinc-400 py-6 text-center">Loading agents…</div>
          ) : agentsError ? (
            <div className="text-sm text-red-600 py-6 text-center">{agentsError}</div>
          ) : agents.length === 0 ? (
            <div className="text-sm text-zinc-400 py-6 text-center">No agents found.</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                    <th className="px-3 py-2 font-medium">Agent</th>
                    <th className="px-3 py-2 font-medium">Zoom User ID</th>
                    <th className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr key={agent.id} className="border-b border-zinc-100 dark:border-zinc-800">
                      <td className="px-3 py-2">
                        <p className="font-medium text-zinc-900 dark:text-zinc-100">
                          {agent.firstName} {agent.lastName}
                        </p>
                        <p className="text-zinc-400">{agent.email}</p>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          placeholder="KDcuGjCZQh2aBc8d9ef"
                          value={agentEdits[agent.id] ?? ""}
                          onChange={(e) =>
                            setAgentEdits((prev) => ({ ...prev, [agent.id]: e.target.value }))
                          }
                          className="text-xs border border-zinc-300 rounded px-2 py-1 w-56 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <SecondaryButton
                          className="!text-xs !px-3 !py-1"
                          onClick={() => handleSaveAgent(agent.id)}
                          disabled={savingAgentId === agent.id}
                        >
                          {savingAgentId === agent.id ? "Saving…" : "Save"}
                        </SecondaryButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isZoomActive && !loading && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              Zoom Phone isn't your active voice provider yet — save your Webhook Settings on the Setup tab
              first so inbound calls are routed to these mappings.
            </p>
          )}
        </SectionCard>
      )}

      {activeTab === "log" && (
        <SectionCard title="Activity Log">
          <IntegrationActivityLog integrationSlug="ZoomPhone" />
        </SectionCard>
      )}
    </StickyRouteHeader>
  );
}
